/**
 * RE-1603 (CIPA Phase 3) — visitor privacy UI: "Your Privacy Choices"
 * footer control + modal, the opt-in consent banner, (Phase 7 addendum) the
 * opt_out first-visit notice banner, and (RE-1796 Phase 3) the GPC
 * disclosure banner shown on none / notice when the visitor's Global Privacy
 * Control signal is what turned tracking off.
 *
 * Loaded from resources/views/partials/privacy-choices.blade.php, which
 * renders whenever privacy_ui_required() is true (RE-1796): the posture puts
 * its own banner on the page (opt_out / opt_in), or the visitor sent a signal
 * (GPC, or a recorded granted/denied choice) on any posture. The banners
 * below stay posture-specific; the control and modal do not.
 *
 * Depends on remora-head.js (window._rem) for the single decision core
 * (computeTrackingPermitted) and cookie access; this module must never re-implement the precedence rules.
 *
 * Reload rule: any recorded choice that changes the effective tracking state
 * reloads the page. There is no dynamic tracker-loading path. Confirming the
 * current state records the cookie but does not reload.
 *
 * RE-1635 (CIPA Phase 1): the tracking-storage flush is allowlist-based.
 * While tracking is denied, every first-party cookie and localStorage/
 * sessionStorage key NOT on the functional allowlist
 * (config/privacy-exemptions.php functional_cookies / functional_storage,
 * emitted by output_head_js() as _rem.privacyFlush) is removed — on a
 * denied choice before the reload, and idempotently on every denied
 * pageload. Exempt-provider storage (storage_identities in the same
 * config) always survives; the FlushDeniedCookies response middleware
 * consumes the same config sections server-side.
 */
window._remPrivacy = {
  cookieName: 'remora_privacy_consent',
  endpoint: '/privacy-consent',
  // The open shadow root that isolates the privacy UI from page CSS and
  // scripts; populated by mount(). el() resolves every UI node against it.
  root: null,
  // Functional dismissal state for the opt_out notice banner, mirroring the
  // legacy notice banner's localStorage acknowledge pattern (cookie-consent).
  // On the functional_storage allowlist in config/privacy-exemptions.php so
  // it survives the denied-state flush.
  dismissKey: 'privacy-notice-dismissed',
  // Dismissal state for the GPC disclosure banner. Deliberately a SEPARATE
  // key from dismissKey (RE-1796): the two banners say different things, and
  // localStorage is per origin, so one key would let a dismissal recorded on
  // this site suppress the other banner on this same site later. The
  // populations are disjoint at any one moment (that banner is opt_out's,
  // this one is none/notice's) but not over time: the dealer can change
  // posture under a returning visitor, and the visitor can turn GPC on or
  // off, either of which swaps which banner they are owed while the old
  // key is still sitting in storage.
  // Also on the functional_storage allowlist, and it has to be: this banner's
  // audience is denied visitors, so the denied-state flush runs on every one
  // of their pageloads.
  gpcDismissKey: 'privacy-gpc-dismissed',

  // ---- Pure decision helpers (covered by tests/js/privacy-choices.test.js) ----

  // The opt-in banner prompts until an explicit granted/denied choice exists,
  // and returns every pageview when ignored (no acknowledgment is stored).
  shouldShowBanner: function (posture, storedChoice) {
    return posture === 'opt_in' && storedChoice !== 'granted' && storedChoice !== 'denied';
  },

  // The opt_out notice banner shows on first visit and keeps returning until
  // it is dismissed or an explicit choice is recorded (either value, via the
  // banner or the modal). GPC never suppresses it: the banner is informational
  // and its opt-out action is how a GPC visitor records a sticky denied.
  shouldShowNoticeBanner: function (posture, storedChoice, dismissed) {
    return (
      posture === 'opt_out' &&
      storedChoice !== 'granted' &&
      storedChoice !== 'denied' &&
      dismissed !== true
    );
  },

  // True when tracking is off because of the browser's Global Privacy
  // Control signal rather than an explicit visitor choice. RE-1796: GPC is
  // honored in every posture, so this no longer filters on posture.
  //
  // `posture` is now unused and deliberately kept. Every pure predicate here
  // (shouldShowBanner, shouldShowNoticeBanner, controlState, shouldReload,
  // modalView) takes the same (posture, storedChoice, gpcEnabled) triple, and
  // modalView forwards its own arguments straight through. Dropping the
  // parameter would make this one predicate call differently from its
  // siblings to save nothing.
  gpcBlocking: function (posture, storedChoice, gpcEnabled) {
    return storedChoice !== 'granted' && storedChoice !== 'denied' && gpcEnabled === true;
  },

  // The GPC disclosure banner (RE-1796 Phase 3). Shown only where GPC is what
  // denies tracking AND the posture puts no banner of its own on the page:
  // on opt_out and opt_in the visitor already has a banner offering the same
  // choice, and a second sheet would stack on it.
  //
  // Deliberately expressed as gpcBlocking() plus a posture list plus the
  // dismissal, rather than re-deriving "no stored choice and GPC is on". That
  // condition already has one name, and it also drives the modal's GPC note;
  // two spellings of it would drift.
  shouldShowGpcBanner: function (posture, storedChoice, gpcEnabled, dismissed) {
    return (
      (posture === 'none' || posture === 'notice') &&
      this.gpcBlocking(posture, storedChoice, gpcEnabled) &&
      dismissed !== true
    );
  },

  // Reload only when recording newChoice changes the effective state.
  shouldReload: function (posture, storedChoice, gpcEnabled, newChoice) {
    var before = window._rem.computeTrackingPermitted(posture, storedChoice, gpcEnabled);
    var after = window._rem.computeTrackingPermitted(posture, newChoice, gpcEnabled);
    return before !== after;
  },

  // Modal action labels are driven by the STORED choice, not the effective
  // state: "Keep ..." labels only appear when they reflect a recorded
  // explicit choice; every click still records. With no stored choice (state
  // from the posture default or GPC) both labels stay imperative, because
  // clicking either records a brand-new explicit choice.
  modalLabels: function (storedChoice) {
    if (storedChoice === 'granted') {
      return { enable: 'Keep tracking', disable: 'Turn off tracking' };
    }
    if (storedChoice === 'denied') {
      return { enable: 'Allow tracking', disable: 'Keep tracking off' };
    }
    return { enable: 'Allow tracking', disable: 'Turn off tracking' };
  },

  // RE-1635 (CIPA Phase 4): footer control state readout. Driven by the
  // EFFECTIVE tracking state (stored choice > GPC > posture default), so
  // after a deny-and-reload the control visibly reads Off, matching the
  // noscript banner's "currently OFF" wording. Off-only per design review:
  // no readout while tracking is permitted.
  controlState: function (posture, storedChoice, gpcEnabled) {
    return window._rem.computeTrackingPermitted(posture, storedChoice, gpcEnabled) ? '' : 'Off';
  },

  modalView: function (posture, storedChoice, gpcEnabled) {
    return {
      permitted: window._rem.computeTrackingPermitted(posture, storedChoice, gpcEnabled),
      gpcBlocking: this.gpcBlocking(posture, storedChoice, gpcEnabled),
      labels: this.modalLabels(storedChoice),
    };
  },

  // ---- Runtime state accessors ----

  posture: function () {
    return window._rem ? window._rem.privacyPosture : 'none';
  },

  storedChoice: function () {
    return window._rem.getCookie(this.cookieName);
  },

  // RE-1796: either GPC channel counts - the browser's navigator property, or
  // the Sec-GPC request header the server observed and emitted as
  // _rem.gpcHeader. Delegated to remora-head.js for the same reason the
  // precedence core is: this module must never re-implement a decision input,
  // or the two sides can disagree about the same visitor.
  gpcEnabled: function () {
    return window._rem ? window._rem.gpcSignalled() : false;
  },

  noticeDismissed: function () {
    return window.localStorage.getItem(this.dismissKey) !== null;
  },

  // Dismissal is purely functional: it hides the banner persistently but
  // records no consent choice and writes no consent cookie. That is exactly
  // what separates dismissed from optout in the event taxonomy (RE-1796).
  dismissNoticeBanner: function () {
    window.localStorage.setItem(this.dismissKey, 'acknowledged');
    this.emit('privacy_banner_dismissed', 'notice');
    this.hideNoticeBanner();
  },

  gpcDismissed: function () {
    return window.localStorage.getItem(this.gpcDismissKey) !== null;
  },

  // Same contract as dismissNoticeBanner: functional state only. The visitor
  // acknowledged a disclosure; they did not choose anything, so no consent
  // cookie is written and the event is dismissed rather than optout.
  dismissGpcBanner: function () {
    window.localStorage.setItem(this.gpcDismissKey, 'acknowledged');
    this.emit('privacy_banner_dismissed', 'gpc');
    this.hideGpcBanner();
  },

  // ---- RE-1796 Phase 1: Remora-only privacy analytics events ----

  // sessionStorage guard prefix for the once-per-visit shown events. Carries a
  // functional_storage PREFIX entry in config/privacy-exemptions.php, or the
  // denied-state flush deletes it on exactly the visitors it exists for.
  shownKeyPrefix: 'privacy-shown-',

  // Matomo's event name field: <posture>|<surface>|<gpc>. Read live rather
  // than captured, so a label always describes the state at emit time.
  eventLabel: function (surface) {
    return this.posture() + '|' + surface + '|' + (this.gpcEnabled() ? '1' : '0');
  },

  // Every event here goes to Remora's Matomo, and nowhere else unless the
  // domain has opted into the gtag mirror below.
  // thirdPartyAllowed: false suppresses the gtag leg unconditionally; the
  // Matomo leg is exempt from the emission gate (RE-1743 PRD 6.1, counsel
  // advised and product decided 2026-07-31, extended to client-side events
  // 2026-08-03), which is why these fire for denied visitors at all. That
  // exemption does not extend to the mirror, which carries its own gate.
  emit: function (action, surface) {
    if (!window._rem || typeof window._rem.emitEvent !== 'function') {
      return;
    }
    window._rem.emitEvent(
      action,
      {},
      {
        matomo: ['trackEvent', 'Privacy', action, this.eventLabel(surface)],
        thirdPartyAllowed: false,
      },
    );
    this.sendToGtag(action, surface);
  },

  // Opt-in per-domain mirror of the event above into the dealer's own Google
  // tag surface, emitted through gtag(). Enabled by
  // tp_google_tag_manager_privacy_events, which output_head_js() emits as
  // _rem.privacyEventsToGtag only when the Google Tag Manager toggle is also
  // on, so privacy events stay Matomo-only on every other domain.
  //
  // gtag is defined here rather than assumed present. Our Google Tag Manager
  // snippet (the google_tag_manager third party) defines dataLayer and nothing
  // else; gtag appears only once a Google tag inside the container has loaded,
  // which races the banner's own shown event and never happens at all for a
  // container holding no Google tag. Installing Google's documented shim first
  // makes the call order-independent, because the shim is a dataLayer push and
  // dataLayer is a plain array GTM drains on init.
  //
  // An existing window.gtag is called, never replaced. On a domain also
  // running the google_analytics third party that global is that snippet's own
  // shim, bound to the dealer's GA4 property: reassigning it would break their
  // GA4 tagging, and calling it delivers the privacy event to that property as
  // well as into the container. That co-delivery is requested behavior, not a
  // side effect to be designed around: the dealer who asked for the gtag leg
  // wants these events in their GA4 property. A later change must not route
  // around it to keep the events container-only.
  //
  // Still NOT the gtag leg of _rem.emitEvent() via thirdPartyAllowed: true,
  // which installs no shim of its own, skips silently whenever gtag is
  // undefined, and carries no params.
  //
  // The event name matches the Matomo action, which is what lines the two
  // reports up.
  //
  // Gated on trackingPermitted() like every other third-party sink: the
  // RE-1743 exemption covers Remora's own tracker alone, so a visitor who has
  // already denied emits nothing. No typeof guard on trackingPermitted,
  // because output_head_js() emits the whole _rem payload or none of it and
  // never a hollow stub, so a present _rem is a loaded bundle.
  sendToGtag: function (action, surface) {
    if (!window._rem || !window._rem.privacyEventsToGtag || !window._rem.trackingPermitted()) {
      return;
    }
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
    }
    window.gtag('event', action, {
      remoraPrivacyPosture: this.posture(),
      remoraPrivacySurface: surface,
      remoraPrivacyGpc: this.gpcEnabled(),
    });
  },

  // Shown events fire once per VISIT, not once per pageview. Both banners
  // re-render on every pageview until dismissed or answered, so a per-pageview
  // event would count one visitor seeing one banner many times over. That is
  // not what the metric means, and once per visit is the grain Matomo's own
  // reporting is visit-scoped to.
  //
  // Deliberately NOT justified on queue capacity, which is the wrong reason and
  // an easy one to reach for. The single-worker drain rate that argument rests
  // on (5.65 sets/s, nine hours of arrivals above it on 2026-08-03) was an
  // artifact of the Provider plugin doing a blocking reverse-DNS lookup per
  // visit, ~96% of the drain's cost. With that gone the queue clears every
  // tick against fleet arrivals. See bare-metal/docs/rules/matomo.md, section
  // `The drain is wait-bound`, which caveats those numbers directly.
  emitOncePerVisit: function (action, surface) {
    var key = this.shownKeyPrefix + surface;
    try {
      if (window.sessionStorage.getItem(key) !== null) {
        return;
      }
      window.sessionStorage.setItem(key, '1');
    } catch (storageError) {
      // Private mode or a quota failure leaves the guard unavailable. Emit
      // anyway: an over-count is recoverable in reporting, a silent zero for
      // a whole browser class is not.
    }
    this.emit(action, surface);
  },

  // ---- Consent recording ----

  recordChoice: function (choice) {
    var tokenMeta = document.querySelector('meta[name="csrf-token"]');
    return fetch(this.endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-CSRF-TOKEN': tokenMeta ? tokenMeta.getAttribute('content') : '',
      },
      body: 'value=' + encodeURIComponent(choice),
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('privacy-consent request failed: ' + response.status);
      }
      return response;
    });
  },

  // ---- Tracking-storage flush (RE-1603, CIPA Phase 6) ----

  // Pure: does name match an {exact, prefix} rule set from the config?
  matchesRules: function (name, rules) {
    if (!rules) return false;
    var exact = rules.exact || [];
    var prefix = rules.prefix || [];
    for (var i = 0; i < exact.length; i++) {
      if (name === exact[i]) return true;
    }
    for (var j = 0; j < prefix.length; j++) {
      if (name.indexOf(prefix[j]) === 0) return true;
    }
    return false;
  },

  // Pure: names to flush = everything NOT matching the functional allowlist
  // and NOT matching an exempt-provider identity. The consent cookie is
  // functional (the choice record itself, also allowlisted) — never flushed.
  flushTargets: function (names, allowRules, exemptRules) {
    var targets = [];
    for (var i = 0; i < names.length; i++) {
      var name = names[i];
      if (name === this.cookieName) continue;
      if (this.matchesRules(name, allowRules)) continue;
      if (this.matchesRules(name, exemptRules)) continue;
      targets.push(name);
    }
    return targets;
  },

  cookieNames: function () {
    var names = [];
    var parts = document.cookie.split(';');
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].trim();
      if (part === '') continue;
      var name = part.split('=')[0];
      if (names.indexOf(name) === -1) names.push(name);
    }
    return names;
  },

  // Expire a cookie host-only and on every parent-domain candidate: vendors
  // (GA et al.) set dot-domain cookies on the registrable domain, mirroring
  // how ConsentCookieScope scopes the consent cookie server-side. Setting a
  // Domain the browser rejects (public suffix) is silently ignored.
  expireCookie: function (name) {
    var expired = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    document.cookie = expired;
    var parts = window.location.hostname.split('.');
    for (var i = 0; i < parts.length - 1; i++) {
      var domain = parts.slice(i).join('.');
      document.cookie = expired + '; domain=' + domain;
      document.cookie = expired + '; domain=.' + domain;
    }
  },

  storageKeys: function (area) {
    var keys = [];
    for (var i = 0; i < area.length; i++) {
      keys.push(area.key(i));
    }
    return keys;
  },

  // Remove every cookie and storage key not on the functional allowlist.
  // config is injectable for tests; the page uses the server-emitted
  // _rem.privacyFlush (config/privacy-exemptions.php functional allowlist +
  // exemption storage identities). Idempotent and cheap: nothing is written
  // when everything present is allowlisted.
  flushTrackingStorage: function (config) {
    config = config || (window._rem && window._rem.privacyFlush);
    if (!config || !config.allowlist) return;
    var allow = config.allowlist;
    var exempt = config.exemptions || {};

    var cookieTargets = this.flushTargets(this.cookieNames(), allow.cookies, exempt.cookies);
    for (var i = 0; i < cookieTargets.length; i++) {
      this.expireCookie(cookieTargets[i]);
    }

    var areas = [window.localStorage, window.sessionStorage];
    for (var j = 0; j < areas.length; j++) {
      var targets = this.flushTargets(this.storageKeys(areas[j]), allow.storage, exempt.storage);
      for (var k = 0; k < targets.length; k++) {
        areas[j].removeItem(targets[k]);
      }
    }
  },

  // Record a choice, then reload only if the effective state changed.
  //
  // surface names where the choice was made ('consent' | 'notice' | 'modal').
  // It selects between the banner and choices event families and feeds the
  // event label, so it is required rather than optional: the same 'denied'
  // travels through here from four different controls.
  choose: function (choice, surface) {
    var _this = this;
    var posture = this.posture();
    var storedChoice = this.storedChoice();
    var gpc = this.gpcEnabled();
    var reload = this.shouldReload(posture, storedChoice, gpc, choice);

    if (surface === 'modal') {
      this.emit(choice === 'granted' ? 'privacy_choices_optin' : 'privacy_choices_optout', surface);
    } else {
      this.emit(choice === 'granted' ? 'privacy_banner_optin' : 'privacy_banner_optout', surface);
    }

    // A mode event is exactly a change in EFFECTIVE state, which is exactly
    // what shouldReload already computes. Recording a choice that matches the
    // current state (confirming a posture default, or a durable denied while
    // GPC already denies) fires the choice event but no mode event.
    //
    // privacy_mode_enabled means privacy mode is on, i.e. tracking is now
    // DENIED; privacy_mode_disabled means tracking is now permitted.
    if (reload) {
      this.emit(
        window._rem.computeTrackingPermitted(posture, choice, gpc)
          ? 'privacy_mode_disabled'
          : 'privacy_mode_enabled',
        surface,
      );
    }

    return this.recordChoice(choice).then(function () {
      // Flush before any reload so a denied visitor never carries tracking
      // storage into the next (suppressed) pageview.
      if (choice === 'denied') {
        _this.flushTrackingStorage();
      }
      if (reload) {
        window.location.reload();
        return;
      }
      _this.hideBanner();
      _this.hideNoticeBanner();
      // The GPC banner's own visitor is the likeliest caller to land here: a
      // durable `denied` recorded while GPC already denies changes no
      // effective state, so nothing reloads and only this call takes the
      // banner they just answered off the screen.
      _this.hideGpcBanner();
      _this.closeModal();
    });
  },

  // ---- UI wiring ----

  el: function (id) {
    return this.root ? this.root.getElementById(id) : null;
  },

  // Build the isolated UI: clone the inert <template> into an OPEN shadow
  // root on #privacy-choices-root so dealer/theme custom CSS and scripts can
  // neither restyle nor reach the banner, control, or modal. The host is
  // reparented to the end of <body> so it lives in the root stacking context
  // (never trapped inside a themed ancestor's) with an inline !important
  // max z-index, then promoted to the browser top layer (RE-1695), which
  // paints above ANY z-index in ANY DOM order. Browsers without the popover
  // API fall back to the equal-z DOM-order tiebreak, defended by
  // keepHostLast. Idempotent: a second call is a no-op.
  mount: function () {
    var host = document.getElementById('privacy-choices-root');
    if (!host) {
      return;
    }
    if (host.shadowRoot) {
      this.root = host.shadowRoot;
      return;
    }
    var template = document.getElementById('privacy-choices-template');
    if (!template) {
      return;
    }
    var root = host.attachShadow({ mode: 'open' });
    root.appendChild(template.content.cloneNode(true));
    document.body.appendChild(host);
    host.style.setProperty('z-index', '2147483647', 'important');
    if (!this.promoteToTopLayer(host)) {
      // position: relative gives the host its own stacking context without
      // becoming the containing block for the fixed-position children inside
      // the shadow root (which must stay viewport-anchored).
      host.style.setProperty('position', 'relative', 'important');
      this.keepHostLast(host);
    }
    this.root = root;
  },

  // RE-1695: third-party widgets (chat launchers, onsite-engagement footers)
  // also pin themselves at the 32-bit-max z-index and append themselves to
  // <body> after this host mounts, winning the equal-z DOM-order tiebreak
  // and painting over the banner – on palmenkia.com that hid the reject
  // button, which is a compliance failure. The top layer beats any z-index
  // and any DOM order, so the host is shown as a manual popover (no light
  // dismiss, no ESC). The host stretches across the viewport but stays
  // hit-test inert; the shadow children restore their own pointer-events in
  // the template CSS. Returns false when the popover API is unavailable.
  promoteToTopLayer: function (host) {
    if (typeof host.showPopover !== 'function') {
      return false;
    }
    host.setAttribute('popover', 'manual');
    // Neutralize the popover UA styles (fit-content box centered by auto
    // margins, border, padding, canvas background) into a transparent
    // full-viewport sheet.
    host.style.setProperty('position', 'fixed', 'important');
    host.style.setProperty('inset', '0', 'important');
    host.style.setProperty('width', 'auto', 'important');
    host.style.setProperty('height', 'auto', 'important');
    host.style.setProperty('margin', '0', 'important');
    host.style.setProperty('padding', '0', 'important');
    host.style.setProperty('border', 'none', 'important');
    host.style.setProperty('background', 'transparent', 'important');
    host.style.setProperty('overflow', 'visible', 'important');
    host.style.setProperty('pointer-events', 'none', 'important');
    try {
      host.showPopover();
    } catch (mountError) {
      // Not connected / already open edge cases: demote cleanly. A lingering
      // popover attribute would UA-style the host to display: none and erase
      // the privacy UI entirely.
      host.removeAttribute('popover');
      host.style.setProperty('position', 'relative', 'important');
      return false;
    }
    // Anything hiding the popover (a stray hidePopover call, another manual
    // popover toggling it) would likewise display: none the host. Demote to
    // the DOM-order fallback instead of vanishing.
    var _this = this;
    host.addEventListener('toggle', function (event) {
      if (event.newState === 'closed') {
        host.removeAttribute('popover');
        _this.keepHostLast(host);
      }
    });
    return true;
  },

  // Fallback ordering guard where no top layer is available: keep the host
  // the last <body> child so it wins the equal-z-index DOM-order tiebreak
  // against widgets appended after mount. Re-appends are capped so a widget
  // fighting for the same slot cannot loop the page.
  keepHostLast: function (host) {
    if (typeof MutationObserver === 'undefined') {
      return;
    }
    var remainingReappends = 20;
    var observer = new MutationObserver(function () {
      // A host removed from the document was removed deliberately (page
      // teardown, navigation libraries); guarding it back in would fight
      // that, so stand down instead of resurrecting the node.
      if (!host.isConnected) {
        observer.disconnect();
        return;
      }
      if (document.body.lastElementChild === host) {
        return;
      }
      if (remainingReappends <= 0) {
        observer.disconnect();
        return;
      }
      remainingReappends--;
      document.body.appendChild(host);
    });
    observer.observe(document.body, { childList: true });
  },

  renderControlState: function () {
    var state = this.el('privacy-choices-state');
    if (!state) return;
    state.textContent = this.controlState(this.posture(), this.storedChoice(), this.gpcEnabled());
    state.hidden = state.textContent === '';
  },

  showBanner: function () {
    var banner = this.el('privacy-optin-banner');
    if (banner) banner.hidden = false;
  },

  hideBanner: function () {
    var banner = this.el('privacy-optin-banner');
    if (banner) banner.hidden = true;
  },

  showNoticeBanner: function () {
    var banner = this.el('privacy-optout-banner');
    if (banner) banner.hidden = false;
  },

  hideNoticeBanner: function () {
    var banner = this.el('privacy-optout-banner');
    if (banner) banner.hidden = true;
  },

  showGpcBanner: function () {
    var banner = this.el('privacy-gpc-banner');
    if (banner) banner.hidden = false;
  },

  hideGpcBanner: function () {
    var banner = this.el('privacy-gpc-banner');
    if (banner) banner.hidden = true;
  },

  openModal: function () {
    this.renderModal();
    var overlay = this.el('privacy-choices-modal');
    if (overlay) overlay.hidden = false;
    // Opening the modal is a deliberate visitor action, so unlike the banner
    // shown events this fires on every open rather than once per visit.
    this.emit('privacy_choices_shown', 'modal');
  },

  closeModal: function () {
    var overlay = this.el('privacy-choices-modal');
    if (overlay) overlay.hidden = true;
  },

  renderModal: function () {
    var view = this.modalView(this.posture(), this.storedChoice(), this.gpcEnabled());

    var status = this.el('privacy-modal-status');
    if (status) {
      status.textContent = view.permitted
        ? 'Analytics and tracking for advertising are currently ON.'
        : 'Analytics and tracking for advertising are currently OFF.';
    }

    var gpcNote = this.el('privacy-modal-gpc-note');
    if (gpcNote) gpcNote.hidden = !view.gpcBlocking;

    // Both actions stay available in every state: picking the action that
    // matches the current state records a durable explicit choice (useful
    // when the state comes from a default or the GPC signal) without a
    // reload; picking the other one flips the state and reloads. Labels are
    // render-time only (see modalLabels); the handlers and their
    // record/reload semantics are identical in every state.
    var enable = this.el('privacy-modal-enable');
    if (enable) enable.textContent = view.labels.enable;
    var disable = this.el('privacy-modal-disable');
    if (disable) disable.textContent = view.labels.disable;
  },

  bind: function (id, handler) {
    var element = this.el(id);
    if (element) {
      element.addEventListener('click', handler);
    }
  },

  init: function () {
    var _this = this;
    // RE-1796: no posture check here - the server already decided this UI
    // renders (privacy_ui_required() in app/Core/Helpers.php gates the
    // partial). The _rem guard is NOT redundant with the posture check it
    // replaces: output_head_js() may fail to read the minified head bundle
    // and leave the page with no window._rem at all (the RE-1775 contract is
    // that such a page goes inert, not that it throws), and the old posture
    // check stood in for that silently, because posture() reports 'none' when
    // _rem is absent. Without this the trackingPermitted() call below would
    // be a TypeError.
    if (!window._rem) {
      return;
    }
    var posture = this.posture();

    // Idempotent pageload flush: tracking storage never survives a denied
    // pageview, however it got there (pre-denial residue, vendor drift).
    if (!window._rem.trackingPermitted()) {
      this.flushTrackingStorage();
    }

    // Everything below drives shadow-root nodes; without the isolated UI
    // there is nothing to wire.
    this.mount();
    if (!this.root) {
      return;
    }

    // Any choice that changes the effective state reloads the page, so
    // rendering the state readout once at init keeps it accurate.
    this.renderControlState();

    this.bind('privacy-choices-control', function () {
      _this.openModal();
    });
    // Closing the modal without recording anything is a dismissal. The event
    // lives on these two handlers rather than inside closeModal(), because
    // choose() also calls closeModal() after a recorded choice and that is
    // not a dismissal.
    this.bind('privacy-modal-close', function () {
      _this.emit('privacy_choices_dismissed', 'modal');
      _this.closeModal();
    });
    var overlay = this.el('privacy-choices-modal');
    if (overlay) {
      overlay.addEventListener('click', function (event) {
        if (event.target === overlay) {
          _this.emit('privacy_choices_dismissed', 'modal');
          _this.closeModal();
        }
      });
    }
    this.bind('privacy-modal-enable', function () {
      _this.choose('granted', 'modal');
    });
    this.bind('privacy-modal-disable', function () {
      _this.choose('denied', 'modal');
    });

    // Opt-in banner: accept records granted; close (X) records denied; a
    // visitor who ignores it records nothing and is prompted next pageview.
    this.bind('privacy-optin-accept', function () {
      _this.choose('granted', 'consent');
    });
    // The opt-in banner's X RECORDS denied, so it is an optout event, not a
    // dismissal. This is the one place the two families diverge by control
    // rather than by surface.
    this.bind('privacy-optin-close', function () {
      _this.choose('denied', 'consent');
    });

    if (this.shouldShowBanner(posture, this.storedChoice())) {
      this.showBanner();
      this.emitOncePerVisit('privacy_banner_shown', 'consent');
    }

    // opt_out notice banner: X dismisses without recording anything;
    // "Accept All" records granted and the secondary reject action records
    // denied, both through the normal choose flow (reload only when the
    // effective state changes); the "Your Privacy Choices" link opens the
    // modal.
    this.bind('privacy-optout-close', function () {
      _this.dismissNoticeBanner();
    });
    this.bind('privacy-optout-accept', function () {
      _this.choose('granted', 'notice');
    });
    this.bind('privacy-optout-optout', function () {
      _this.choose('denied', 'notice');
    });
    this.bind('privacy-optout-choices', function () {
      _this.openModal();
    });

    if (this.shouldShowNoticeBanner(posture, this.storedChoice(), this.noticeDismissed())) {
      this.showNoticeBanner();
      this.emitOncePerVisit('privacy_banner_shown', 'notice');
    }

    // GPC disclosure banner: the X dismisses without recording anything, and
    // the banner's own "Your Privacy Choices" button opens the modal, because
    // the banner paints over the footer control its copy names.
    this.bind('privacy-gpc-close', function () {
      _this.dismissGpcBanner();
    });
    this.bind('privacy-gpc-choices', function () {
      _this.openModal();
    });

    if (
      this.shouldShowGpcBanner(posture, this.storedChoice(), this.gpcEnabled(), this.gpcDismissed())
    ) {
      this.showGpcBanner();
      this.emitOncePerVisit('privacy_gpc_shown', 'gpc');
    }
  },
};

if (typeof document !== 'undefined' && document.readyState !== 'loading') {
  window._remPrivacy.init();
} else if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', function () {
    window._remPrivacy.init();
  });
}
